import {
  type Context,
  type SuccessfulRequestContextResponse,
  type ValidateRequestContextResult,
} from './types'
import { MultipleContextsErrorState, NoContextsErrorState } from './errorState'

/**
 * Normalizes a Context object to ensure mutual exclusivity between requestContext and adminRequestContext.
 *
 * When `adminRequestContext` is present, it takes precedence and `requestContext` is set to `undefined`.
 * When `adminRequestContext` is not present, `requestContext` retains its original value.
 *
 * @param context - The input Context object containing request context information
 * @returns A new Context object with the same properties, but with mutually exclusive
 *          requestContext and adminRequestContext based on the following rules:
 *          - If `adminRequestContext` is defined: `requestContext` becomes `undefined`
 *          - If `adminRequestContext` is undefined: `requestContext` retains its original value
 *
 * @example
 * ```typescript
 * // When adminRequestContext is defined
 * const contextWithAdmin = {
 *   requestContext: { clientConfigurationId: 1, storeConfigurationId: 2, countryId: 3 },
 *   adminRequestContext: { retailerAppId: '123' }
 * }
 * const result = normalizedRequestContext(contextWithAdmin)
 * // result.requestContext will be undefined
 * // result.adminRequestContext will be { retailerAppId: '123' }
 *
 * // When adminRequestContext is undefined
 * const contextWithoutAdmin = {
 *   requestContext: { clientConfigurationId: 1, storeConfigurationId: 2, countryId: 3 },
 *   adminRequestContext: undefined
 * }
 * const result = normalizedRequestContext(contextWithoutAdmin)
 * // result.requestContext will be { clientConfigurationId: 1, storeConfigurationId: 2, countryId: 3 }
 * // result.adminRequestContext will be undefined
 * ```
 */
export const normalizedRequestContext = (context: Context): Context => ({
  adminRequestContext: context.adminRequestContext,
  requestContext: context.adminRequestContext ? undefined : context.requestContext,
})

/**
 * This function performs validation checks on the request context data to ensure:
 * 1. Multiple request contexts are only allowed when admin context is present
 * 2. At least one request context must be available
 *
 * @param requestContextResponse - The successful response from a request context query containing
 *                                 the available request contexts and current context information
 * @returns A validation result object indicating whether the request context is valid and
 *          providing the appropriate error state component if validation fails
 *
 * @example
 * ```typescript
 * const requestContextResponse = useGetValidRequestContexts({ retailerId, partnerId })
 * // ... ensure requestContextResponse is successful
 * const { isRequestContextInvalid, InvalidContextErrorState } = validateRequestContext(
 *   requestContextResponse
 * )
 * if (isRequestContextInvalid) {
 *   return <InvalidContextErrorState />
 * }
 * ```
 */
export const validateRequestContext = (
  requestContextResponse: SuccessfulRequestContextResponse
): ValidateRequestContextResult => {
  const { requestContexts, context } = requestContextResponse.data

  if (requestContexts.length > 1 && !context?.adminRequestContext) {
    return {
      isRequestContextInvalid: true,
      InvalidContextErrorState: MultipleContextsErrorState,
    }
  }

  if (requestContexts.length === 0) {
    return {
      isRequestContextInvalid: true,
      InvalidContextErrorState: NoContextsErrorState,
    }
  }

  return {
    isRequestContextInvalid: false,
    InvalidContextErrorState: null,
  }
}
